import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { Icons } from '../components/Icons';

function AttributeManagement() {
  const { user } = useAuth();
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'text',
    options: []
  });
  const [optionInput, setOptionInput] = useState('');

  // 속성 불러오기
  useEffect(() => {
    fetchAttributes();
  }, []);

  const fetchAttributes = async () => {
    setLoading(true);
    try {
      const attributesRef = collection(db, 'attributes');
      const snapshot = await getDocs(attributesRef);

      const attributeList = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      setAttributes(attributeList);
    } catch (error) {
      console.error('속성 불러오기 오류:', error);
      alert('속성을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 속성 추가
  const handleAddAttribute = async () => {
    if (!formData.name.trim()) {
      alert('속성 이름을 입력하세요.');
      return;
    }

    if (formData.type === 'select' && formData.options.length === 0) {
      alert('선택형 속성은 최소 1개의 옵션이 필요합니다.');
      return;
    }

    try {
      await addDoc(collection(db, 'attributes'), {
        name: formData.name,
        type: formData.type,
        options: formData.options,
        createdAt: serverTimestamp(),
        createdBy: user.email
      });

      alert('✅ 속성이 추가되었습니다.');
      setShowAddModal(false);
      resetForm();
      fetchAttributes();
    } catch (error) {
      console.error('속성 추가 오류:', error);
      alert('속성 추가 중 오류가 발생했습니다.');
    }
  };

  // 속성 수정
  const handleUpdateAttribute = async () => {
    if (!formData.name.trim()) {
      alert('속성 이름을 입력하세요.');
      return;
    }

    try {
      const attributeRef = doc(db, 'attributes', editingAttribute.id);
      await updateDoc(attributeRef, {
        name: formData.name,
        type: formData.type,
        options: formData.options,
        updatedAt: serverTimestamp(),
        updatedBy: user.email
      });

      alert('✅ 속성이 수정되었습니다.');
      setEditingAttribute(null);
      resetForm();
      fetchAttributes();
    } catch (error) {
      console.error('속성 수정 오류:', error);
      alert('속성 수정 중 오류가 발생했습니다.');
    }
  };

  // 속성 삭제
  const handleDeleteAttribute = async (attributeId, attributeName) => {
    if (!window.confirm(`"${attributeName}" 속성을 삭제하시겠습니까?\n\n이 속성을 사용하는 제품이 있을 수 있습니다.`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'attributes', attributeId));
      alert('✅ 속성이 삭제되었습니다.');
      fetchAttributes();
    } catch (error) {
      console.error('속성 삭제 오류:', error);
      alert('속성 삭제 중 오류가 발생했습니다.');
    }
  };

  // 옵션 추가
  const handleAddOption = () => {
    if (!optionInput.trim()) {
      alert('옵션 값을 입력하세요.');
      return;
    }

    if (formData.options.includes(optionInput.trim())) {
      alert('이미 존재하는 옵션입니다.');
      return;
    }

    setFormData({
      ...formData,
      options: [...formData.options, optionInput.trim()]
    });
    setOptionInput('');
  };

  // 옵션 제거
  const handleRemoveOption = (option) => {
    setFormData({
      ...formData,
      options: formData.options.filter(opt => opt !== option)
    });
  };

  // 수정 시작
  const startEdit = (attribute) => {
    setEditingAttribute(attribute);
    setFormData({
      name: attribute.name,
      type: attribute.type,
      options: attribute.options || []
    });
  };

  // 폼 초기화
  const resetForm = () => {
    setFormData({
      name: '',
      type: 'text',
      options: []
    });
    setOptionInput('');
  };

  // 모달 닫기
  const closeModal = () => {
    setShowAddModal(false);
    setEditingAttribute(null);
    resetForm();
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-3xl font-bold text-brand-900">속성 관리</h2>
          <p className="text-gray-500 mt-1">제품 속성을 관리합니다.</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition font-bold flex items-center shadow-md"
        >
          <Icons.Plus className="w-5 h-5 mr-2" />
          속성 추가
        </button>
      </div>

      {/* 속성 목록 */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">속성을 불러오는 중...</p>
        </div>
      ) : attributes.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Icons.Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg mb-4">등록된 속성이 없습니다.</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition font-bold"
          >
            첫 속성 추가하기
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-brand-50 border-b border-brand-100">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-bold text-brand-900">이름</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-brand-900">종류</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-brand-900">옵션</th>
                <th className="px-6 py-4 text-right text-sm font-bold text-brand-900">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {attributes.map((attribute) => (
                <tr key={attribute.id} className="hover:bg-gray-50 transition">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Icons.Tag className="w-5 h-5 text-brand-500 mr-2" />
                      <span className="font-bold text-gray-900">{attribute.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                      attribute.type === 'select' ? 'bg-purple-100 text-purple-700' :
                      attribute.type === 'text' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {attribute.type === 'select' ? '선택형' :
                       attribute.type === 'text' ? '텍스트' :
                       attribute.type === 'number' ? '숫자' : '날짜'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {attribute.type === 'select' && attribute.options?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {attribute.options.slice(0, 3).map((option, idx) => (
                          <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                            {option}
                          </span>
                        ))}
                        {attribute.options.length > 3 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded">
                            +{attribute.options.length - 3}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => startEdit(attribute)}
                        className="px-3 py-1.5 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg transition text-sm font-medium"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => handleDeleteAttribute(attribute.id, attribute.name)}
                        className="px-3 py-1.5 bg-white hover:bg-red-50 border border-gray-300 hover:border-red-300 text-gray-700 hover:text-red-600 rounded-lg transition text-sm font-medium"
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 추가/수정 모달 */}
      {(showAddModal || editingAttribute) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">
                {editingAttribute ? '속성 수정' : '속성 추가'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <Icons.X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* 속성 이름 */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  속성 이름 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="예: 사이즈, 컬러, 재조사..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>

              {/* 속성 종류 */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  속성 종류 <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value, options: [] })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                >
                  <option value="text">텍스트</option>
                  <option value="select">선택형 (드롭다운)</option>
                  <option value="number">숫자</option>
                  <option value="date">날짜</option>
                </select>
              </div>

              {/* 선택형 옵션 */}
              {formData.type === 'select' && (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    옵션 목록 <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={optionInput}
                      onChange={(e) => setOptionInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddOption()}
                      placeholder="옵션 값 입력 (예: 텍스트, 조각, 날짜)"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    />
                    <button
                      onClick={handleAddOption}
                      className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition font-bold"
                    >
                      추가
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.options.map((option, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 px-3 py-2 bg-brand-50 border border-brand-200 text-brand-700 rounded-lg"
                      >
                        <span className="font-medium">{option}</span>
                        <button
                          onClick={() => handleRemoveOption(option)}
                          className="text-brand-400 hover:text-brand-600 transition"
                        >
                          <Icons.X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  {formData.options.length === 0 && (
                    <p className="text-sm text-gray-500 mt-2">옵션을 추가하세요.</p>
                  )}
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                onClick={closeModal}
                className="px-6 py-3 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg transition font-bold"
              >
                취소
              </button>
              <button
                onClick={editingAttribute ? handleUpdateAttribute : handleAddAttribute}
                className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition font-bold"
              >
                {editingAttribute ? '수정' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AttributeManagement;
